import FirstName from "@/app/components/common/actions/inputs-b/FirstName";
import AddressCommon from "@/app/components/common/actions/inputs-b/AddressCommon";
import { ICON_BASE_PATH } from "@/app/utils/config";
import CountryCode from "@/app/components/common/actions/inputs-b/CountryCode";
import Landline from "@/app/components/common/actions/inputs-b/Landline";
import LicenseUpload from "@/app/components/common/actions/inputs-b/LicenseUpload";
import { useEffect } from "react";
import type { UseFormSetValue, UseFormWatch } from "react-hook-form";
import DatePickerInput from "@/app/components/common/actions/inputs-b/DatePickerInput";
import ProfileStatements from "./ProfileStatements";
import "../UserProfile.css";

interface FileUploadChangeEvent {
  value: File | null;
}

interface Props {
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  company: any;
  updateSection: "address" | "landline" | "poboxInfo" | "renewnow" | null;
}

export interface SmeFileUploadResponseData {
  id: number;
  userId: number;
  bankId: number;
  smeCompId: number;
  fileName: string;
  filePath: string | null;
  type: string | null;
  documentType: string;
  base64Output: string | null;
  menuId: number;
  permissionId: number;
}

export interface SmeFileUploadApiResponse {
  status: number;
  data?: SmeFileUploadResponseData;
  errorMessage?: string;
}

export const TRADE_LICENSE_DOC_TYPE = "TRADE LICENSE";

// ............................... formatRenewalDate ................................
export const formatRenewalDate = (date: string | Date): string => {
  const d = new Date(date);
  const pad = (value: number, length = 2) =>
    String(value).padStart(length, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const UpdateCompanyInfo = ({
  setValue,
  watch,

  company,
  updateSection,
}: Props) => {
  // ............................... handleFileChange ................................
  const handleFileChange = (event: FileUploadChangeEvent): void => {
    const file = event.value;

    if (!file) return;

    setValue("tradeLicenseFile", file);
    setValue("tradeLicenseFileName", file.name);
  };

  // ............................... useEffect  ................................
  useEffect(() => {
    if (company !== null) {
      setValue("addressLine1", company.addressLine1 ?? "");
      setValue("addressLine2", company.addressLine2 ?? "");
      setValue("addressLine3", company.addressLine3 ?? "");
      setValue("addressLine4", company.addressLine4 ?? "");
      setValue("countryCode2", company.countryCode2 ?? "");
      setValue("companyLandline", company.companyLandline ?? "");
      setValue("companyPoBox", company.companyPoBox ?? "");
      setValue("newenddate", company.tradeLicenseExpiryDate ?? "");
      setValue("tradeLicenseFilePath", company.tradeLicenseFilePath ?? "");
      setValue("tradeLicenseExpiryDate", null);
      setValue("tradeLicenseFile", null);
    }
  }, [company, setValue]);

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* .....................address........................ */}
      {updateSection === "address" && (
        <>
          <div className="col-span-12">
            <AddressCommon
              label="Address 1"
              value={watch("addressLine1")}
              onChange={(val) => setValue("addressLine1", val)}
            />
          </div>
          <div className="col-span-12">
            <AddressCommon
              label="Address 2"
              value={watch("addressLine2")}
              onChange={(val) => setValue("addressLine2", val)}
            />
          </div>
          <div className="col-span-12">
            <AddressCommon
              label="Address 3"
              value={watch("addressLine3")}
              onChange={(val) => setValue("addressLine3", val)}
            />
          </div>
          <div className="col-span-12">
            <AddressCommon
              label="Address 4"
              value={watch("addressLine4")}
              onChange={(val) => setValue("addressLine4", val)}
            />
          </div>
          <div className="col-span-12">
            <CountryCode
              label="Country Code"
              value={watch("countryCode2")}
              onChange={(val) => setValue("countryCode2", val)}
            />
          </div>
        </>
      )}

      {/* .....................landline........................ */}
      {updateSection === "landline" && (
        <div className="col-span-12">
          <Landline
            label="Company Landline"
            value={watch("companyLandline")}
            onChange={(val) => setValue("companyLandline", val)}
          />
        </div>
      )}

      {/* .....................poboxInfo........................ */}
      {updateSection === "poboxInfo" && (
        <div className="col-span-12">
          <FirstName
            label="company Po Box"
            value={watch("companyPoBox")}
            onChange={(val) => setValue("companyPoBox", val)}
          />
        </div>
      )}

      {updateSection === "renewnow" && (
        <>
          <div className="col-span-12">
            <DatePickerInput
              label="Current End Date"
              value={watch("newenddate")}
              onChange={(val) => {
                setValue("newenddate", val || "");
              }}
              iconSrc={`${ICON_BASE_PATH}date.svg`}
            />
          </div>
          <div className="col-span-12">
            <DatePickerInput
              label="New End Date"
              value={watch("tradeLicenseExpiryDate")}
              onChange={(val) => {
                setValue("tradeLicenseExpiryDate", val || "");
              }}
              iconSrc={`${ICON_BASE_PATH}date.svg`}
            />
          </div>

          <div className="gx-upload-license col-span-12">
            <div className="gx-upload-license-container">
              <h3 className="gx-upload-license-title gx-p14-b">
                <span>Upload Renewed License</span>
                <span className="gx-p14-b gx-required">*</span>
              </h3>

              <span className="gx-upload-Choose-fIle gx-p12-md">
                Choose FIle
              </span>
            </div>

            <LicenseUpload
              onChange={handleFileChange}
              label="Upload Trade License"
            />
          </div>

          <div className="col-span-12 mt-4">
            <ProfileStatements
              vcifId={company?.vcifId}
              docTypes={[TRADE_LICENSE_DOC_TYPE]}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default UpdateCompanyInfo;

export function base64ToUint8Array(base64: string): Uint8Array {
  const raw = atob(base64);
  const rawLength = raw.length;
  const array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; i++) {
    array[i] = raw.charCodeAt(i);
  }

  return array;
}
